import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatShortDate, formatDateTime } from '../utils/datetime';
import RecipeInterview, { RecipeValues, toRecipeBody } from '../components/RecipeInterview';
import { useAppStore } from '../App';
import { useFileTranscodeStore } from '../stores/fileTranscodeStore';
import { transcodeBadge } from '../utils/transcodeBadge';
import { FileDetailModal, MoFile } from '../components/FileDetailModal';

interface DashboardStats {
 activeCampaigns: number;
 familyCount: number;
 fileCount: number;
}

interface ActivityLog {
 entity_type: string;
 entity_name: string;
 action: string;
 actor_name: string;
 created_at: string;
}

interface SystemRelease {
 id: number;
 component: string;
 version: string;
 platform: string;
 nix_store_path: string | null;
 deploy_level: number;
 created_at: string;
}

interface RenderHubLog {
 t: number;
 level: 'info' | 'warn' | 'error';
 message: string;
}

interface RenderHubWorker {
 id: string;
 caps: { av1Vaapi: boolean; hw: string };
 currentJobId: string | null;
 registeredAt: number;
 lastPingAt: number;
 recentLogs: RenderHubLog[];
}

interface RenderHubStatus {
 workers: RenderHubWorker[];
 queueDepth: number;
 inFlightCount: number;
 timestamp: number;
}

interface RenderHubSnapshot {
 status: RenderHubStatus | null;
 receivedAt: number;
 bridgeConnected: boolean;
 enabled: boolean;
}

const ENTITY_LABELS: Record<string, string> = {
 mofamily: '모패밀리',
 campaign: '캠페인',
 mofile: '영상',
 moclient: '스크린',
};

const ACTION_LABELS: Record<string, string> = {
 created: '생성',
 updated: '수정',
 deleted: '삭제',
 invited: '초대 요청',
 accepted: '초대 수락',
 barter_matched: '바터 체결',
 barter_ended: '바터 종료',
 sudo_start: '관리자 view-as 진입',
 sudo_end: '관리자 view-as 종료',
};

const formatAgo = (ms: number): string => {
 if (!ms) return '—';
 const d = Date.now() - ms;
 if (d < 60_000) return `${Math.max(1, Math.floor(d / 1000))}초 전`;
 if (d < 3_600_000) return `${Math.floor(d / 60_000)}분 전`;
 return `${Math.floor(d / 3_600_000)}시간 전`;
};

const formatClockTime = (ms: number): string => {
 const d = new Date(ms);
 return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
};

const LOG_LEVEL_COLOR: Record<string, string> = {
 info: 'text-gray-700',
 warn: 'text-yellow-700',
 error: 'text-red-700',
};

interface NodeHeartbeat {
 node_id: string;
 // 등록 정보 — heartbeat 는 **미등록 장비도** 만들므로 셋 다 null 일 수 있고,
 // null 인 것 자체가 "아직 어느 모패밀리에도 안 붙었다"는 신호다.
 screen_name: string | null;
 family_name: string | null;
 client_hash: string | null;
 platform: string;
 momanager_version: string;
 uptime_secs: number;
 last_seen: string;
 nix_store_free_bytes: number | null;
 nix_store_total_bytes: number | null;
 status: 'online' | 'offline';
}

const fmtBytes = (b: number | null): string => {
 if (b == null) return '-';
 const gb = b / 1e9;
 return gb >= 1 ? `${gb.toFixed(1)}GB` : `${(b / 1e6).toFixed(0)}MB`;
};
const fmtUptime = (s: number): string => {
 if (!s) return '-';
 const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
 return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// 장비 heartbeat (MoManager node_id 기준, 등록과 무관) — /moupdate/nodes.
const NodesWidget: React.FC<{ nodes: NodeHeartbeat[] }> = ({ nodes }) => {
 return (
  <div className="bg-white border border-gray-200 overflow-x-auto">
   <table className="min-w-full text-sm">
    <thead className="bg-gray-50 text-gray-600">
     <tr>
      <th className="px-3 py-2 text-left font-medium">상태</th>
      <th className="px-3 py-2 text-left font-medium">화면 / 모패밀리</th>
      <th className="px-3 py-2 text-left font-medium">node_id</th>
      <th className="px-3 py-2 text-left font-medium">platform</th>
      <th className="px-3 py-2 text-left font-medium">MoManager</th>
      <th className="px-3 py-2 text-left font-medium">uptime</th>
      <th className="px-3 py-2 text-left font-medium">/nix 여유</th>
      <th className="px-3 py-2 text-left font-medium">마지막 접속</th>
     </tr>
    </thead>
    <tbody>
     {nodes.map((n) => (
      <tr key={n.node_id} className="border-t border-gray-100">
       <td className="px-3 py-2">
        {/* dot 색으로 상태 표시 — 온라인/오프라인 텍스트는 생략(툴팁으로 보조). */}
        <span
          title={n.status === 'online' ? '온라인' : '오프라인'}
          className={`inline-block w-2 h-2 rounded-full ${n.status === 'online' ? 'bg-green-500' : 'bg-gray-300'}`}
        />
       </td>
       <td className="px-3 py-2">
        {n.screen_name || n.family_name ? (
         <>
          <div className="text-gray-900">{n.screen_name || '(이름 없음)'}</div>
          <div className="text-xs text-gray-500">{n.family_name || '(모패밀리 없음)'}</div>
         </>
        ) : (
         <span className="text-xs text-gray-400" title="아직 등록되지 않았거나, 등록 후 한 번도 WS 채널에 붙지 않았습니다.">미등록</span>
        )}
       </td>
       <td className="px-3 py-2 font-mono text-xs text-gray-800">{n.node_id}</td>
       <td className="px-3 py-2 text-gray-600">{n.platform}</td>
       <td className="px-3 py-2 text-gray-600">{n.momanager_version}</td>
       <td className="px-3 py-2 text-gray-600">{fmtUptime(n.uptime_secs)}</td>
       <td className="px-3 py-2 text-gray-600">
        {fmtBytes(n.nix_store_free_bytes)}{n.nix_store_total_bytes ? ` / ${fmtBytes(n.nix_store_total_bytes)}` : ''}
       </td>
       <td className="px-3 py-2 text-gray-500 text-xs">{new Date(n.last_seen).toLocaleString('ko-KR')}</td>
      </tr>
     ))}
     {nodes.length === 0 && (
      <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-400">heartbeat 없음</td></tr>
     )}
    </tbody>
   </table>
  </div>
 );
};

const RenderHubWidget: React.FC<{ snapshot: RenderHubSnapshot }> = ({ snapshot }) => {
 if (!snapshot.enabled) {
  return (
   <div className="bg-gray-50 p-4 text-sm text-gray-600">
    RenderHub 비활성 (MOcombinator가 로컬 ffmpeg로 인코딩 중)
   </div>
  );
 }
 if (!snapshot.bridgeConnected) {
  return (
   <div className="bg-red-50 p-4 text-sm text-red-700">
    RenderHub 서비스에 연결되지 않음 — mocombinator-renderhub.service 상태 확인 필요
   </div>
  );
 }
 if (!snapshot.status) {
  return (
   <div className="bg-yellow-50 p-4 text-sm text-yellow-700">
    연결됨, 상태 수신 대기 중…
   </div>
  );
 }
 const { workers, queueDepth, inFlightCount } = snapshot.status;
 return (
  <div className="bg-gray-50 p-2 md:p-4">
   <div className="grid grid-cols-3 gap-2 md:gap-4 mb-3 md:mb-4">
    <div>
     <div className="text-xs md:text-sm text-gray-500">워커</div>
     <div className="text-lg md:text-2xl font-semibold text-gray-800">{workers.length}</div>
    </div>
    <div>
     <div className="text-xs md:text-sm text-gray-500">진행 중</div>
     <div className="text-lg md:text-2xl font-semibold text-gray-800">{inFlightCount}</div>
    </div>
    <div>
     <div className="text-xs md:text-sm text-gray-500">큐 대기</div>
     <div className="text-lg md:text-2xl font-semibold text-gray-800">{queueDepth}</div>
    </div>
   </div>
   {workers.length === 0 ? (
    <div className="text-xs text-gray-500">원격 워커 없음 — OCI 로컬 ffmpeg 폴백으로 처리됨</div>
   ) : (
    <>
     {/* 모바일: 카드 리스트 (세로 스택). 6컬럼 테이블이 가로 오버플로우 나는 것 방지. */}
     <div className="space-y-3 md:hidden">
      {workers.map(w => (
       <div key={w.id} className="bg-white border border-gray-200 p-2 text-xs">
        <div className="flex items-center justify-between gap-2 mb-1">
         <span className="font-mono text-[10px] truncate flex-1" title={w.id}>{w.id}</span>
         <span className={`px-1.5 py-0.5 text-[10px] flex-shrink-0 ${w.caps.av1Vaapi ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
          {w.caps.av1Vaapi ? 'AV1 HW' : 'AV1 SW'}
         </span>
        </div>
        <div className="text-[10px] text-gray-600 truncate" title={w.caps.hw}>HW: {w.caps.hw}</div>
        <div className="mt-1">
         <span className="text-gray-500">상태: </span>
         {w.currentJobId
          ? <span className="text-blue-700 font-mono">{w.currentJobId}</span>
          : <span className="text-gray-400">idle</span>}
        </div>
        <div className="text-[10px] text-gray-500 mt-0.5">
         연결 {formatAgo(w.registeredAt)} · ping {formatAgo(w.lastPingAt)}
        </div>
        {w.recentLogs && w.recentLogs.length > 0 && (
         <div className="mt-2 bg-gray-50 border border-gray-100 p-1 font-mono text-[10px] leading-tight max-h-32 overflow-y-auto">
          {w.recentLogs.slice(-10).map((log, i) => (
           <div key={i} className={LOG_LEVEL_COLOR[log.level] || 'text-gray-600'}>
            <span className="text-gray-400 pr-1">{formatClockTime(log.t)}</span>
            {log.message}
           </div>
          ))}
         </div>
        )}
       </div>
      ))}
     </div>

     {/* 데스크탑: 기존 테이블 */}
     <table className="w-full text-sm hidden md:table">
      <thead>
       <tr className="border-b border-gray-200 text-left text-gray-500">
        <th className="py-1 pr-3">ID</th>
        <th className="py-1 pr-3">HW</th>
        <th className="py-1 pr-3">AV1 VAAPI</th>
        <th className="py-1 pr-3">상태</th>
        <th className="py-1 pr-3">연결 후</th>
        <th className="py-1">마지막 ping</th>
       </tr>
      </thead>
      <tbody>
       {workers.map(w => (
        <React.Fragment key={w.id}>
        <tr className="border-b border-gray-100">
         <td className="py-1 pr-3 font-mono text-xs">{w.id}</td>
         <td className="py-1 pr-3 text-xs text-gray-600 max-w-xs truncate" title={w.caps.hw}>{w.caps.hw}</td>
         <td className="py-1 pr-3">
          <span className={`px-2 py-0.5 text-xs ${w.caps.av1Vaapi ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
           {w.caps.av1Vaapi ? 'HW' : 'SW'}
          </span>
         </td>
         <td className="py-1 pr-3">
          {w.currentJobId ? (
           <span className="text-blue-700 font-mono text-xs">{w.currentJobId}</span>
          ) : (
           <span className="text-gray-400">idle</span>
          )}
         </td>
         <td className="py-1 pr-3 text-xs text-gray-500">{formatAgo(w.registeredAt)}</td>
         <td className="py-1 text-xs text-gray-500">{formatAgo(w.lastPingAt)}</td>
        </tr>
        {w.recentLogs && w.recentLogs.length > 0 && (
         <tr>
          <td colSpan={6} className="py-2 pr-3">
           <div className="bg-white border border-gray-200 p-2 font-mono text-[11px] leading-tight overflow-x-auto">
            {w.recentLogs.slice(-10).map((log, i) => (
             <div key={i} className={LOG_LEVEL_COLOR[log.level] || 'text-gray-600'}>
              <span className="text-gray-400 pr-2">{formatClockTime(log.t)}</span>
              {log.message}
             </div>
            ))}
           </div>
          </td>
         </tr>
        )}
        </React.Fragment>
       ))}
      </tbody>
     </table>
    </>
   )}
   <div className="text-xs text-gray-400 mt-3">
    마지막 갱신: {formatAgo(snapshot.receivedAt)}
   </div>
  </div>
 );
};

// 비로그인 익명 업로드 대기 항목(GET /pending-uploads).
interface PendingBox { id: number; name: string; }
interface PendingItem {
 id: number;
 fileHash: string | null;
 eventStartAt: string | null;
 promoStartAt: string | null;
 eventLabel: string | null;
 overlayPosition: number;
 createdAt: string;
 filename: string | null;
 thumbnailPath: string | null;
 duration: number | null;
 mimeType: string | null;
 fileSize: number | null;
 expiresAt: string | null;
 // 게재 준비 상태(mo_files) — 배지·승인 가능 여부.
 transcodeStatus?: string | null;
 transcodeProgress?: number | null;
 preparing?: boolean | null;
 transcodeError?: string | null;
 source?: string | null;   // 'api'(인스타 자동수집)·'plugin'·'public'·null(레거시)
 eventEndAt?: string | null;
 suggestedBox: PendingBox | null;
}


// 운영처 자료 — 영상과 무관한 계정 첨부(격리 /attachments 모듈). 자기완결: 목록·직접 업로드·공개토글·삭제·다운로드.
type AcctMaterial = { id: number; filename: string; isPublic: boolean; byteSize: number; createdAt?: string };
const acctFmtBytes = (b: number): string =>
 b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const acctIsVideo = (f: string): boolean => /\.(mp4|webm|mov|m4v)$/i.test(f);
const acctVideoMime = (f: string): string =>
 /\.webm$/i.test(f) ? 'video/webm' : /\.mov$/i.test(f) ? 'video/quicktime' : /\.m4v$/i.test(f) ? 'video/x-m4v' : 'video/mp4';
const AcctVideoThumb: React.FC<{ id: number }> = ({ id }) => {
 const [ok, setOk] = useState(true);
 if (!ok) return null;
 return <img src={`/attachments/attachment/${id}/thumbnail`} onError={() => setOk(false)} alt=""
  className="w-16 h-10 object-cover rounded bg-black shrink-0" />;
};
const AccountMaterialsSection: React.FC = () => {
 const [items, setItems] = useState<AcctMaterial[]>([]);
 const [loading, setLoading] = useState(false);
 const [busy, setBusy] = useState<number | null>(null);
 const [uploading, setUploading] = useState(false);
 const [err, setErr] = useState('');
 const inputRef = React.useRef<HTMLInputElement | null>(null);
 const [playing, setPlaying] = useState<{ id: number; url: string } | null>(null);
 const playUrlRef = React.useRef<string | null>(null);
 useEffect(() => () => { if (playUrlRef.current) URL.revokeObjectURL(playUrlRef.current); }, []);
 const play = async (a: AcctMaterial) => {
  if (playing?.id === a.id) {
   if (playUrlRef.current) { URL.revokeObjectURL(playUrlRef.current); playUrlRef.current = null; }
   setPlaying(null); return;
  }
  setErr('');
  try {
   const r = await fetch(`/attachments/attachment/${a.id}/download`, { credentials: 'include' });
   if (!r.ok) { setErr('재생할 수 없어요.'); return; }
   const blob = new Blob([await r.blob()], { type: acctVideoMime(a.filename) });
   const url = URL.createObjectURL(blob);
   if (playUrlRef.current) URL.revokeObjectURL(playUrlRef.current);
   playUrlRef.current = url; setPlaying({ id: a.id, url });
  } catch { setErr('재생할 수 없어요.'); }
 };
 const load = () => {
  setLoading(true);
  fetch('/attachments/account', { credentials: 'include' })
   .then((r) => (r.ok ? r.json() : { data: [] }))
   .then((d) => setItems(d.data ?? []))
   .catch(() => setItems([]))
   .finally(() => setLoading(false));
 };
 useEffect(() => { load(); }, []);
 const [files, setFiles] = useState<File[]>([]); // 선택만 하고 '올리기' 버튼으로 전송
 const upload = async () => {
  if (files.length === 0) return;
  setErr(''); setUploading(true);
  try {
   for (const f of files) {
    const fd = new FormData();
    fd.append('file', f);
    fd.append('is_public', 'false'); // 기본 비공개 — 올린 뒤 공개 토글
    const r = await fetch('/attachments/account', { method: 'POST', credentials: 'include', body: fd });
    const d = await r.json().catch(() => ({}));
    if (!(r.ok && d.success)) setErr(d.error || '자료 업로드에 실패했어요.');
   }
   load();
  } finally { setUploading(false); setFiles([]); if (inputRef.current) inputRef.current.value = ''; }
 };
 const toggle = async (a: AcctMaterial) => {
  setBusy(a.id);
  try {
   const r = await fetch(`/attachments/attachment/${a.id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    credentials: 'include', body: JSON.stringify({ is_public: !a.isPublic }),
   });
   if (r.ok) setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, isPublic: !a.isPublic } : x)));
  } finally { setBusy(null); }
 };
 const remove = async (a: AcctMaterial) => {
  if (!window.confirm(`'${a.filename}' 자료를 삭제할까요?`)) return;
  setBusy(a.id);
  try {
   const r = await fetch(`/attachments/attachment/${a.id}`, { method: 'DELETE', credentials: 'include' });
   if (r.ok) setItems((prev) => prev.filter((x) => x.id !== a.id));
  } finally { setBusy(null); }
 };
 return (
  <div className="bg-white border border-gray-200 p-4 mb-6">
   <div className="flex items-center justify-between mb-2">
    <h2 className="text-xl font-semibold text-gray-800">운영처 자료</h2>
    {loading && <span className="text-xs text-gray-400">불러오는 중…</span>}
   </div>
   <p className="text-xs text-gray-500 mb-3">영상과 무관한 문서·파일입니다. 공개로 두면 업로드 페이지에서 내려받을 수 있어요. (파일당 50MB)</p>
   <input ref={inputRef} type="file" multiple disabled={uploading}
    onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
    className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-300 file:bg-gray-50 file:text-sm mb-2" />
   {files.length > 0 && (
    <div className="mb-2">
     <p className="text-xs text-gray-500 truncate">{files.map((f) => f.name).join(', ')}</p>
     <button type="button" onClick={() => upload()} disabled={uploading}
      className="mt-2 px-4 py-1.5 bg-blue-500 text-white rounded text-sm disabled:opacity-50 hover:bg-blue-600 transition-colors">
      {uploading ? '올리는 중…' : `올리기 (${files.length}개)`}
     </button>
    </div>
   )}
   {err && <p className="text-xs text-red-500 mb-2">{err}</p>}
   {items.length === 0 ? (
    <p className="text-xs text-gray-400">등록된 자료가 없습니다.</p>
   ) : (
    <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
     {items.map((a) => (
      <li key={a.id} className="px-2 py-1.5 text-sm">
       <div className="flex items-center gap-2">
        <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${a.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{a.isPublic ? '공개' : '비공개'}</span>
        {acctIsVideo(a.filename) && <AcctVideoThumb id={a.id} />}
        <span className="flex-1 truncate text-gray-700" title={a.filename}>{a.filename}</span>
        <span className="text-xs text-gray-400 shrink-0">{acctFmtBytes(a.byteSize)}</span>
        {acctIsVideo(a.filename) && (
         <button type="button" onClick={() => play(a)} className="text-xs text-indigo-600 hover:underline shrink-0">{playing?.id === a.id ? '■ 닫기' : '▶ 재생'}</button>
        )}
        <a href={`/attachments/attachment/${a.id}/download`} className="text-xs text-blue-600 hover:underline shrink-0">⬇ 내려받기</a>
        <button type="button" disabled={busy === a.id} onClick={() => toggle(a)}
         className="text-xs text-gray-500 hover:text-green-600 shrink-0 disabled:opacity-50">{a.isPublic ? '비공개로' : '공개로'}</button>
        <button type="button" disabled={busy === a.id} onClick={() => remove(a)}
         className="text-xs text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-50">삭제</button>
       </div>
       {playing?.id === a.id && (
        <video src={playing.url} controls autoPlay className="w-full mt-2 rounded bg-black"
         onError={() => setErr('이 브라우저에서 미리보기를 지원하지 않는 형식입니다 — 내려받아 확인하세요.')} />
       )}
      </li>
     ))}
    </ul>
   )}
  </div>
 );
};

const DashboardPage: React.FC = () => {
 const navigate = useNavigate();
 const user = useAppStore(s => s.user);
 // 관리자 판정은 Layout.tsx(관리자 메뉴)와 동일해야 한다 — 관리자는 isAdmin(boolean) 플래그로
 // 식별되며 role 이 'admin' 이 아닐 수 있다. role 만 보면 isAdmin 관리자에게 MoRenderer/릴리스/
 // 스크린 섹션이 통째로 숨겨진다(회귀).
 const isAdmin = user?.isAdmin || user?.role === 'admin';
 const [stats, setStats] = useState<DashboardStats>({
 activeCampaigns: 0,
 familyCount: 0,
 fileCount: 0
 });
 const [activities, setActivities] = useState<ActivityLog[]>([]);
 const [systemReleases, setSystemReleases] = useState<SystemRelease[]>([]);
 const [releasesOpen, setReleasesOpen] = useState(false); // 릴리스 배포 순위 접기(길어서 기본 접힘)
 const [renderHub, setRenderHub] = useState<RenderHubSnapshot | null>(null);
 const [nodes, setNodes] = useState<NodeHeartbeat[]>([]);
 const [adUploaderCampaigns, setAdUploaderCampaigns] = useState<any[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [pending, setPending] = useState<PendingItem[]>([]);
 // 승인·편성 문진 모달 대상(퀵UI Stage 3 — 인라인 폼을 공용 문진으로 교체).
 const [approveTarget, setApproveTarget] = useState<PendingItem | null>(null);
 // 대기 영상 미리보기 모달 대상.
 const [previewItem, setPreviewItem] = useState<PendingItem | null>(null);

 const fetchPending = React.useCallback(() => {
  fetch('/pending-uploads', { credentials: 'include' })
   .then(r => r.json())
   .then(j => {
    if (!j?.success) return;
    const items: PendingItem[] = j.data?.items ?? [];
    setPending(items);
    // 대기 파일의 준비 상태를 스토어에 seed — 소유주(=업로더) 탭은 WS 푸시로 이어서 갱신된다.
    const seed = useFileTranscodeStore.getState().seed;
    for (const it of items) {
     const st = it.transcodeStatus;
     if (!it.fileHash || (st !== 'pending' && st !== 'processing' && st !== 'failed' && st !== 'done')) continue;
     seed(it.fileHash, null, { status: st, progress: it.transcodeProgress ?? 0, preparing: it.preparing ?? false, error: it.transcodeError ?? undefined });
    }
   })
   .catch(() => { /* 대기목록 없음/오류는 대시보드 치명 아님 */ });
 }, []);

 // 마운트 + WS 'pending_upload' 알림(tick 변화)마다 refetch — 폴링 없이 이벤트 구동.
 const pendingUploadTick = useAppStore(s => s.pendingUploadTick);
 const transcodeEntries = useFileTranscodeStore(s => s.entries);
 const wsConnected = useAppStore(s => !!s.ws);
 const prepBadgeOf = (it: PendingItem) => transcodeBadge({
  server: { status: it.transcodeStatus, progress: it.transcodeProgress, preparing: it.preparing, error: it.transcodeError },
  entry: it.fileHash ? transcodeEntries.get(`${it.fileHash}::main`) : undefined,
  wsConnected,
 });
 useEffect(() => { fetchPending(); }, [fetchPending, pendingUploadTick]);

 // 문진 프리필 — 종전 인라인 폼 규칙 1:1 보존: 행사=업로더 지정, 종료=expiresAt ?? 행사+1h,
 // 시작=promoStartAt ?? 종료−1주(백엔드 파생 규약과 동일).
 const approvePrefill = (item: PendingItem) => {
  // 097: 업로더가 넣은 만료(expiresAt)는 **행사 종료**다(publicUpload 가 그 의미로 저장한다).
  // 종전엔 이걸 홍보 종료 칸에 넣어 두 시각이 한 칸을 겸했다 — 이제 제자리로 보낸다.
  // 홍보 시작/종료는 비워 둔다: 문진이 행사 시작−7일 / **행사 종료**로 파생한다(단일 규약).
  const eventEndIso = item.expiresAt
   ?? (item.eventStartAt ? new Date(new Date(item.eventStartAt).getTime() + 3600000).toISOString() : null);
  return {
   eventStartAt: item.eventStartAt,
   eventEndAt: eventEndIso,
   promoStartAt: item.promoStartAt,  // 업로더 제안이 있으면 그것, 없으면 문진 파생
   promoEndAt: null,
   // 기본은 **슬롯에 바로**(홍보 일정대로 즉시 재생) — 행사 일시가 있어도 기본은 슬롯.
   // 박스(요일 편성)를 원하면 문진에서 고르면 되고, 그때 요일은 행사 날짜로 자동 제안된다.
   placement: 'slot' as 'box' | 'slot',
   isPublic: false,
  };
 };

 const submitApproveRecipe = async (values: RecipeValues) => {
  if (!approveTarget) return { ok: false, error: '대상 없음' };
  const r = await fetch(`/pending-uploads/${approveTarget.id}/approve`, {
   method: 'POST', credentials: 'include',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ recipe: toRecipeBody(values) }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j?.success) return { ok: false, error: j?.error || '승인에 실패했습니다.' };
  setPending(p => p.filter(x => x.id !== approveTarget.id));
  return { ok: true, receipt: j.data?.receipt ?? { placements: [], warnings: [] } };
 };

 const rejectPending = async (id: number) => {
  if (!confirm('이 업로드를 거절할까요?')) return;
  const r = await fetch(`/pending-uploads/${id}/reject`, { method: 'POST', credentials: 'include' });
  const j = await r.json().catch(() => ({}));
  if (r.ok && j?.success) setPending(p => p.filter(x => x.id !== id));
  else alert(j?.error || '거절에 실패했습니다.');
 };

 useEffect(() => {
  // 내가 업로드한 광고 영상이 송출 중인 캠페인 (read-only)
  fetch('/campaign?as=ad-uploader', { credentials: 'include' })
   .then(r => r.json())
   .then(j => setAdUploaderCampaigns(j?.data ?? []))
   .catch(() => setAdUploaderCampaigns([]));
 }, []);

 useEffect(() => {
 const loadStats = async () => {
 try {
 // Load active campaigns count
 const campaignResponse = await fetch('/campaign', {
 credentials: 'include'
 });
 let activeCampaigns = 0;
 if (campaignResponse.ok) {
 const campaignData = await campaignResponse.json();
 const campaigns = campaignData.data || [];
 const now = new Date();

 // Count campaigns that are active AND currently running
 activeCampaigns = campaigns.filter((campaign: any) => {
 if (!campaign.isActive) return false;
 const start = new Date(campaign.startDatetime);
 const end = new Date(campaign.endDatetime);
 return now >= start && now <= end;
 }).length;
 }

 // Load MoFamily count
 const familyResponse = await fetch('/mofamily/my-families', {
 credentials: 'include'
 });
 if (familyResponse.ok) {
 const familyData = await familyResponse.json();
 const familyCount = familyData.data?.length || 0;

 // Load file count
 const fileResponse = await fetch('/mofile/my-files', {
 credentials: 'include'
 });

 if (fileResponse.ok) {
 const fileData = await fileResponse.json();
 const fileCount = fileData.data?.length || 0;

 setStats(prev => ({
 ...prev,
 activeCampaigns,
 familyCount,
 fileCount
 }));
 }
 }

 // Load activity logs
 const activityResponse = await fetch('/activity-logs?limit=10', {
 credentials: 'include'
 });
 if (activityResponse.ok) {
 const activityData = await activityResponse.json();
 setActivities(activityData.data || []);
 }

 // 시스템 릴리스 + 노드 heartbeat — admin 전용. 비-admin 은 호출 자체를 스킵해
 // 브라우저 콘솔에 403 이 남지 않게 한다(이전엔 try/catch 로 앱 로직만 무시).
 if (isAdmin) {
   try {
     const releasesResponse = await fetch('/moupdate/releases', { credentials: 'include' });
     if (releasesResponse.ok) {
       const releasesData = await releasesResponse.json();
       // ⚠️ 종전엔 `component === 'nixos-system'` 만 걸러 보여줬다. 그래서 실제로 배포를
       //    좌우하는 **컴포넌트 릴리스(moplayer·moclient·momanager·mpp_renderer)를 화면에서
       //    승격할 방법이 아예 없었다.** 그 결과 승격된 aarch64 moplayer 가 2026-04-21 에
       //    멈춰 있었고, 게이트가 정상 동작하는 새 momanager 를 넣은 신품이 그 4월 빌드로
       //    **다운그레이드**됐다(2026-08-08 Screen-223df0 실측: 4월 설치 → 오늘 것 복귀 반복).
       //    게이트가 제대로 돌수록 해가 되는 상태였다 — 승격 화면이 진실을 못 보여줬으니까.
       //    이제 컴포넌트도 같은 표에서 승격한다. 컴포넌트별·플랫폼별 최신 5개.
       const all = (releasesData.data || []) as any[];
       const perGroupCount = new Map<string, number>();
       const limited = all.filter((r: any) => {
         const key = `${r.component}|${r.platform}`;
         const count = perGroupCount.get(key) || 0;
         if (count >= 5) return false;
         perGroupCount.set(key, count + 1);
         return true;
       });
       setSystemReleases(limited);
     }
   } catch { /* ignore */ }

   try {
     const nodesResponse = await fetch('/moupdate/nodes', { credentials: 'include' });
     if (nodesResponse.ok) {
       const nodesData = await nodesResponse.json();
       setNodes(nodesData.data || []);
     }
   } catch { /* ignore */ }
 }
 } catch (error) {
 console.error('Error loading dashboard stats:', error);
 } finally {
 setIsLoading(false);
 }
 };

 loadStats();

 // Admin-only — RenderHub status via Server-Sent Events (event-driven, no polling).
 // 비-admin 은 EventSource 를 아예 만들지 않는다 — 안 그러면 403 후 자동 재연결로 반복 403.
 if (!isAdmin) return;
 const es = new EventSource('/moupdate/renderhub-events', { withCredentials: true });
 const onMessage = (ev: MessageEvent) => {
 try { setRenderHub(JSON.parse(ev.data)); } catch { /* ignore */ }
 };
 es.addEventListener('snapshot', onMessage as EventListener);
 es.addEventListener('status', onMessage as EventListener);
 es.onerror = () => {
 // If we got 403 / 401, EventSource keeps retrying — close permanently.
 if (es.readyState === EventSource.CLOSED) setRenderHub(null);
 };
 return () => { es.close(); };
 }, [isAdmin]);

 const handleDeployLevel = async (id: number, level: number) => {
 try {
  const resp = await fetch(`/moupdate/releases/${id}/deploy-level`, {
   method: 'PATCH',
   headers: { 'Content-Type': 'application/json' },
   credentials: 'include',
   body: JSON.stringify({ deploy_level: level }),
  });
  if (resp.ok) {
   setSystemReleases(prev => prev.map(r => r.id === id ? { ...r, deploy_level: level } : r));
  }
 } catch (e) {
  console.error('Failed to update deploy level:', e);
 }
 };

 const deployLevelLabel = (level: number) => {
 if (level === 0) return '로컬';
 if (level === 1) return '테스트';
 if (level >= 100) return '전체';
 return `L${level}`;
 };

 const deployLevelColor = (level: number) => {
 if (level === 0) return 'bg-gray-200 text-gray-700';
 if (level === 1) return 'bg-yellow-100 text-yellow-800';
 if (level >= 100) return 'bg-green-100 text-green-800';
 return 'bg-blue-100 text-blue-800';
 };

 const formatTime = (dateStr: string) => {
 const date = new Date(dateStr);
 const now = new Date();
 const diffMs = now.getTime() - date.getTime();
 const diffMin = Math.floor(diffMs / 60000);
 if (diffMin < 1) return '방금 전';
 if (diffMin < 60) return `${diffMin}분 전`;
 const diffHour = Math.floor(diffMin / 60);
 if (diffHour < 24) return `${diffHour}시간 전`;
 const diffDay = Math.floor(diffHour / 24);
 if (diffDay < 7) return `${diffDay}일 전`;
 return formatShortDate(date);
 };

 return (
 <div>
 <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
 MOcombinator 대시보드
 </h1>
 <p className="text-gray-600 mb-3">
 스크린, 콘텐츠, 광고 캠페인을 관리하세요
 </p>

 {/* 세션 시각 — 무단 접속 자가 점검 */}
 <div className="bg-blue-50 border border-blue-200 px-3 py-2 mb-4 text-xs md:text-sm text-gray-700 flex flex-wrap gap-x-6 gap-y-1">
  <span>
   <span className="text-gray-500">이전 로그인:</span>{' '}
   <span className="font-mono">{user?.previousLoginAt ? formatDateTime(user.previousLoginAt) : '첫 로그인'}</span>
  </span>
  <span>
   <span className="text-gray-500">마지막 로그아웃:</span>{' '}
   <span className="font-mono">{user?.lastLogoutAt ? formatDateTime(user.lastLogoutAt) : '기록 없음'}</span>
  </span>
  {user?.lastLoginAt && (
   <span>
    <span className="text-gray-500">현재 세션 시작:</span>{' '}
    <span className="font-mono">{formatDateTime(user.lastLoginAt)}</span>
   </span>
  )}
 </div>

 <div className="grid grid-cols-3 gap-2 md:gap-6">
 <div className="bg-gray-100 p-3 md:p-6 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => navigate('/campaign')}>
 <h3 className="text-sm md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">활성 캠페인</h3>
 {isLoading ? (
 <div className="animate-pulse h-7 md:h-9 bg-gray-300 w-12 md:w-16"></div>
 ) : (
 <p className="text-xl md:text-3xl font-bold text-gray-700">{stats.activeCampaigns}</p>
 )}
 </div>
 <div className="bg-gray-100 p-3 md:p-6 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => navigate('/mofamily')}>
 <h3 className="text-sm md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">모패밀리 수</h3>
 {isLoading ? (
 <div className="animate-pulse h-7 md:h-9 bg-gray-300 w-12 md:w-16"></div>
 ) : (
 <p className="text-xl md:text-3xl font-bold text-gray-700">{stats.familyCount}</p>
 )}
 </div>
 <div className="bg-gray-100 p-3 md:p-6 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => navigate('/mofile')}>
 <h3 className="text-sm md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">총 영상</h3>
 {isLoading ? (
 <div className="animate-pulse h-7 md:h-9 bg-gray-300 w-12 md:w-16"></div>
 ) : (
 <p className="text-xl md:text-3xl font-bold text-gray-700">{stats.fileCount}</p>
 )}
 </div>
 </div>

 {/* 비로그인 업로드 대기목록 — 승인 시 영상 박스에 추가, 거절 시 폐기. */}
 {pending.length > 0 && (
 <div className="mt-8">
  <h2 className="text-xl font-semibold text-gray-800 mb-4">업로드 승인 대기 <span className="text-sm font-normal text-gray-400">({pending.length})</span></h2>
  <div className="space-y-2">
   {pending.map(item => (
    <div key={item.id} className="bg-white border border-gray-200 p-2 md:p-3">
     {/* 모바일: 세로 스택 / 데스크톱: 가로. 컨트롤도 모바일에선 각 항목 한 줄씩. */}
     <div className="flex flex-col md:flex-row md:items-center gap-3">
      {/* 썸네일 + 정보 */}
      <div className="flex items-center gap-3 min-w-0 md:flex-1">
       <button type="button" onClick={() => item.fileHash && setPreviewItem(item)} title="클릭하여 재생"
        className="relative w-20 h-12 rounded bg-gray-100 flex-shrink-0 overflow-hidden group">
        {item.thumbnailPath ? (
         <img src={`/mofile/thumbnail/${item.thumbnailPath}`} alt="" className="w-full h-full object-cover" />
        ) : (
         <span className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400">미리보기</span>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity text-white text-lg">▶</span>
       </button>
       <div className="min-w-0 flex-1">
        <button type="button" onClick={() => item.fileHash && setPreviewItem(item)} className="font-medium text-sm truncate block text-left hover:text-blue-600">{item.filename || '(제목 없음)'}</button>
        <div className="text-xs text-gray-500">
          {item.source === 'api' && <span className="inline-block mr-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-medium">이어잇 등록</span>}
          등록 {formatShortDate(item.createdAt)}{item.eventLabel && <> · {item.eventLabel}</>}
        </div>
        {(() => { const b = prepBadgeOf(item); return b.kind === 'none' ? null : (
         <div className={`text-xs mt-0.5 ${b.kind === 'failed' ? 'text-red-600' : 'text-blue-700'}`}>{b.text}{b.error ? ` · ${b.error}` : ''}</div>
        ); })()}
       </div>
      </div>
      {/* 컨트롤 — 승인·편성은 공용 문진 모달(퀵UI)이 담당. 배치(요일 박스·상시 슬롯·
          Public)는 문진 응답이 결정하고 결과는 영수증으로 표시된다. */}
      <div className="flex gap-2 md:flex-shrink-0 md:justify-end">
       <button onClick={() => setApproveTarget(item)}
        disabled={prepBadgeOf(item).kind === 'failed'}
        title={prepBadgeOf(item).kind === 'failed' ? '게재 준비에 실패한 영상은 승인할 수 없습니다. 거절해 주세요.' : undefined}
        className="flex-1 sm:flex-none px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">승인·송출 조건…</button>
       <button onClick={() => rejectPending(item.id)}
        className="flex-1 sm:flex-none px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors">거절</button>
      </div>
     </div>
    </div>
   ))}
  </div>
 </div>
 )}

 {/* 운영처 자료 — 영상 무관 계정 첨부(격리 /attachments 모듈). 자기완결 섹션. */}
 <AccountMaterialsSection />

 {/* 승인·편성 문진(퀵UI) — 프리필은 종전 인라인 폼 규칙 1:1. */}
 {approveTarget && (
  <RecipeInterview
   title={approveTarget.filename || '(제목 없음)'}
   prefill={approvePrefill(approveTarget)}
   approveMode
   onSubmit={submitApproveRecipe}
   onClose={() => setApproveTarget(null)}
  />
 )}

 {/* 대기 영상 상세 — 기존 영상 상세/업로드 모달(FileDetailModal) 재사용. 업로더가 입력한
     시작 시각·동적 라벨·오버레이 위치 + 트랜스코딩 상태를 같은 UI 로 확인/편집. 슬롯 추가
     기능(onAddToEmptySlot)은 승인 흐름과 별개라 미제공. */}
 {previewItem && previewItem.fileHash && (
  <FileDetailModal
   file={{
    title: previewItem.filename || '(제목 없음)',
    file_url: `/mofile/download/${previewItem.fileHash}?v=${previewItem.fileSize ?? 0}`,
    file_hash: previewItem.fileHash,
    file_type: previewItem.mimeType || 'video/mp4',
    created_at: previewItem.createdAt,
    thumbnail_path: previewItem.thumbnailPath || undefined,
    duration: previewItem.duration,
    event_start_at: previewItem.eventStartAt,
    event_label: previewItem.eventLabel,
    overlay_position: previewItem.overlayPosition,
    // 트랜스코딩 상태 전달 — 없으면 모달이 'done'으로 오판해 없는 영상 재생 시도→에러.
    // api 수집본은 승인 전 'deferred'(영상 미변환) → 모달이 포스터 썸네일을 보여준다.
    transcode_status: previewItem.transcodeStatus ?? undefined,
   } as MoFile}
   onClose={() => setPreviewItem(null)}
   onUpdate={(_hash, newTitle) => {
    setPreviewItem(p => (p ? { ...p, filename: newTitle } : p));
    fetchPending();
   }}
   onDelete={() => { setPreviewItem(null); fetchPending(); }}
  />
 )}

 {/* 최근 활동 */}
 <div className="mt-8">
 <h2 className="text-xl font-semibold text-gray-800 mb-4">최근 활동</h2>
 {activities.length === 0 ? (
 <div className="bg-gray-50 p-6">
 <p className="text-gray-500 text-center">아직 활동이 없습니다.</p>
 </div>
 ) : (
 <div className="divide-y divide-gray-100">
 {activities.map((log, idx) => (
 <div key={idx} className="py-3 flex items-center justify-between">
 <div>
 <span className="text-gray-800 font-medium">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
 {log.entity_name && <span className="text-gray-600"> "{log.entity_name}"</span>}
 <span className="text-gray-500"> {ACTION_LABELS[log.action] || log.action}</span>
 </div>
 <span className="text-sm text-gray-400 whitespace-nowrap ml-4">{formatTime(log.created_at)}</span>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* 내 광고가 송출 중인 캠페인 (read-only) — 마켓 Type 1/2 자동 생성된 캠페인 */}
 {adUploaderCampaigns.length > 0 && (
 <div className="mt-6 md:mt-8">
  <h2 className="text-base md:text-xl font-semibold text-gray-800 mb-2 md:mb-4">내 광고가 송출 중인 캠페인</h2>
  <div className="space-y-2">
   {adUploaderCampaigns.map(c => (
    <a
     key={c.campaignHash}
     href={`/campaign/${c.campaignHash}`}
     className="block bg-white border border-gray-200 p-2 md:p-3 hover:border-blue-400 transition-colors"
    >
     <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-sm">{c.title}</span>
      <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-700">읽기 전용</span>
     </div>
     <div className="text-xs text-gray-500 mt-1">
      {c.moFamilyName ? `${c.moFamilyName} · ` : ''}
      {c.startDatetime ? formatDate(c.startDatetime) : ''} ~ {c.endDatetime ? formatDate(c.endDatetime) : ''}
     </div>
    </a>
   ))}
  </div>
 </div>
 )}

 {/* 장비 heartbeat (MoManager node 상태 — 등록 무관) */}
 {nodes.length > 0 && (
 <div className="mt-6 md:mt-8">
  <h2 className="text-base md:text-xl font-semibold text-gray-800 mb-2 md:mb-4">장비 상태 (heartbeat)</h2>
  <NodesWidget nodes={nodes} />
 </div>
 )}

 {/* 시스템 릴리스 관리 */}
 {renderHub && (
 <div className="mt-6 md:mt-8">
  <h2 className="text-base md:text-xl font-semibold text-gray-800 mb-2 md:mb-4">RenderHub</h2>
  <RenderHubWidget snapshot={renderHub} />
 </div>
 )}

 {systemReleases.length > 0 && (
 <div className="mt-8">
  <button
   type="button"
   onClick={() => setReleasesOpen(o => !o)}
   className="flex items-center gap-2 text-xl font-semibold text-gray-800 mb-1 hover:text-gray-600"
  >
   <span className={`inline-block transition-transform ${releasesOpen ? 'rotate-90' : ''}`}>▸</span>
   릴리스 배포 순위 <span className="text-sm font-normal text-gray-400">({systemReleases.length})</span>
   {/* 접힌 상태에서도 '최신인데 전체 승격 안 된' 릴리스가 있으면 경고 — 승격 방치 재발 방지(2026-08 사고). */}
   {!releasesOpen && (() => {
    const seen = new Set<string>(); let warn = false;
    for (const r of systemReleases) { const k = `${r.component}|${r.platform}`; if (!seen.has(k)) { seen.add(k); if (r.deploy_level !== 100) warn = true; } }
    return warn ? <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">승격 확인 필요</span> : null;
   })()}
  </button>
  {releasesOpen && (<>
  <p className="text-xs text-gray-500 mb-4">
   <strong>전체</strong>로 올린 것만 커스터머 장비(배포 순위 100)가 받습니다. 컴포넌트를 올리지 않으면
   장비는 옛 승격본에 묶이고, 최신 이미지로 설치한 신품이 오히려 옛 버전으로 되돌아갑니다.
  </p>
  <div className="overflow-x-auto">
  <table className="w-full text-sm">
   <thead>
   <tr className="border-b border-gray-200 text-left text-gray-500">
    <th className="py-2 pr-4">종류</th>
    <th className="py-2 pr-4">버전</th>
    <th className="py-2 pr-4">플랫폼</th>
    <th className="py-2 pr-4">배포 순위</th>
    <th className="py-2 pr-4">등록일</th>
    <th className="py-2">조작</th>
   </tr>
   </thead>
   <tbody>
   {(() => {
    // 각 (종류, 플랫폼) 조합의 **최신 1건**을 표시한다. 목록은 created_at 내림차순이라
    // 처음 만나는 것이 최신이다. 승격 대상은 거의 항상 이 행들이므로 눈에 띄어야 한다 —
    // 2026-08-08 에 승격본이 3.5개월 방치된 걸 아무도 못 알아챈 이유가 "어느 것이 최신인지
    // 표에서 안 보였다"였다. 신품이 옛 버전으로 다운그레이드되고 나서야 드러났다.
    const seen = new Set<string>();
    const latestIds = new Set<number>();
    for (const r of systemReleases) {
     const key = `${r.component}|${r.platform}`;
     if (!seen.has(key)) { seen.add(key); latestIds.add(r.id); }
    }
    return systemReleases.map(rel => {
    const isLatest = latestIds.has(rel.id);
    return (
    <tr key={rel.id} className={`border-b border-gray-50 ${isLatest ? 'bg-amber-50' : ''}`}>
    <td className="py-2 pr-4">
     <span className={`px-2 py-0.5 text-xs font-medium ${rel.component === 'nixos-system' ? 'bg-purple-100 text-purple-800' : 'bg-blue-50 text-blue-700'}`}>
     {rel.component === 'nixos-system' ? 'OS' : rel.component}
     </span>
    </td>
    <td className="py-2 pr-4 font-mono text-xs">
     {rel.version}
     {isLatest && <span className="ml-2 px-1.5 py-0.5 text-[10px] font-sans font-medium bg-amber-200 text-amber-900 rounded">최신</span>}
    </td>
    <td className="py-2 pr-4">{rel.platform === 'linux-aarch64' ? 'aarch64' : 'x86_64'}</td>
    <td className="py-2 pr-4">
     <span className={`px-2 py-0.5 text-xs font-medium ${deployLevelColor(rel.deploy_level)}`}>
     {deployLevelLabel(rel.deploy_level)}
     </span>
    </td>
    <td className="py-2 pr-4 text-gray-400">{formatTime(rel.created_at)}</td>
    <td className="py-2">
     <div className="flex gap-1">
     {[0, 1, 100].map(level => (
      <button
      key={level}
      onClick={() => handleDeployLevel(rel.id, level)}
      disabled={rel.deploy_level === level}
      className={`px-2 py-0.5 text-xs border ${rel.deploy_level === level ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-white hover:bg-gray-50 text-gray-700 cursor-pointer'}`}
      >
      {deployLevelLabel(level)}
      </button>
     ))}
     </div>
    </td>
    </tr>
    );
    });
   })()}
   </tbody>
  </table>
  </div>
  </>)}
 </div>
 )}
 </div>
 );
};

export default DashboardPage;
